import { useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import {
  HiHome,
  HiUsers,
  HiCube,
  HiDocumentText,
  HiCollection,
  HiChartBar,
  HiChatAlt2,
  HiMenu,
  HiX,
  HiCurrencyDollar,
  HiClipboardList,
  HiTrendingUp,
  HiOfficeBuilding,
  HiTruck,
  HiDocumentAdd,
  HiUser,
  HiCog,
  HiViewList,
  HiReply,
} from 'react-icons/hi'
import { ROUTES } from '@/utils/constants'
import { useAuth } from '@/hooks/useAuth'
import { useStockAlerts } from '@/hooks/useStockAlerts'

const Sidebar = ({ isCollapsed, onToggle, isMobileMenuOpen, onMobileMenuClose }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: alertsData } = useStockAlerts()
  const alertsBadge = alertsData?.length ? String(alertsData.length) : undefined

  // Configuration de la navigation avec rôles requis
  const allNavigationItems = useMemo(() => [
    {
      name: t('nav.dashboard'),
      href: ROUTES.DASHBOARD,
      icon: HiHome,
      // Exclure le caissier: il doit aller directement vers la caisse
      roles: [
        'ROLE_SUPER_ADMIN',
        'ROLE_ADMIN',
        'ROLE_GERANT',
        'ROLE_COMPTABLE',
        'ROLE_MAGASINIER',
        'ROLE_EMPLOYE',
        'ROLE_CAISSE_ADMIN',
      ],
    },
    {
      section: t('nav.sections.sales'),
      items: [
        {
          name: t('nav.clients'),
          href: ROUTES.CLIENTS,
          icon: HiUsers,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_EMPLOYE'], // ✅ Ajouté ROLE_EMPLOYE
        },
        {
          name: t('nav.invoices'),
          href: ROUTES.FACTURES,
          icon: HiDocumentText,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_EMPLOYE'], // ✅ Ajouté ROLE_EMPLOYE
        },
        {
          name: t('nav.deliveryNotes'),
          href: ROUTES.DELIVERY_NOTES,
          icon: HiTruck,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER', 'ROLE_EMPLOYE'],
        },
        {
          name: t('nav.invoiceFromDeliveryNotes'),
          href: ROUTES.DELIVERY_NOTES_INVOICE,
          icon: HiDocumentAdd,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_MAGASINIER', 'ROLE_EMPLOYE'],
        },
        {
          name: t('nav.avoirs'),
          href: ROUTES.AVOIRS,
          icon: HiDocumentText,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_EMPLOYE'],
        },
        {
          name: t('nav.quotes'),
          href: ROUTES.QUOTES,
          icon: HiClipboardList,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_EMPLOYE'], // ✅ Ajouté ROLE_EMPLOYE
        },
        {
          name: t('nav.payments'),
          href: ROUTES.PAYMENTS,
          icon: HiCurrencyDollar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_EMPLOYE'], // ✅ Ajouté ROLE_EMPLOYE
        },
        {
          name: t('nav.paymentRegularization'),
          href: ROUTES.PAYMENTS_REGULARIZATION,
          icon: HiViewList,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_EMPLOYE'],
        },
      ],
    },
    {
      section: t('nav.sections.inventory'),
      items: [
        {
          name: t('nav.products'),
          href: ROUTES.PRODUITS,
          icon: HiCube,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER', 'ROLE_EMPLOYE'], // ✅ Ajouté ROLE_EMPLOYE
        },
        {
          name: t('nav.productLabels'),
          href: ROUTES.PRODUCT_LABELS,
          icon: HiCube,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
        },
        {
          name: t('nav.stockDashboard'),
          href: ROUTES.STOCK_DASHBOARD,
          icon: HiChartBar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
          badge: alertsBadge,
        },
        {
          name: t('nav.stock'),
          href: ROUTES.STOCK,
          icon: HiCollection,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'], // ⚠️ Gardé restrictif (gestion stock sensible)
        },
        {
          name: t('nav.warehouses'),
          href: ROUTES.WAREHOUSES,
          icon: HiOfficeBuilding,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
        },
        {
          name: t('nav.transfers'),
          href: ROUTES.STOCK_TRANSFER,
          icon: HiTrendingUp,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
        },
      ],
    },
    {
      section: t('nav.sections.purchases'),
      items: [
        {
          name: t('nav.purchases'),
          href: ROUTES.ACHATS_DASHBOARD,
          icon: HiClipboardList,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_MAGASINIER'],
        },
        {
          name: t('purchases.requestsMenu', 'Demandes d’achat'),
          href: ROUTES.PURCHASE_REQUESTS,
          icon: HiClipboardList,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_MAGASINIER'],
        },
        {
          name: t('purchases.ordersMenu', 'Commandes fournisseurs'),
          href: ROUTES.PURCHASE_ORDERS,
          icon: HiDocumentText,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_MAGASINIER'],
        },
        {
          name: t('purchases.suppliersMenu', 'Fournisseurs'),
          href: ROUTES.PURCHASE_SUPPLIERS,
          icon: HiUsers,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE', 'ROLE_MAGASINIER'],
        },
        {
          name: t('purchases.receiptsMenu', 'Réceptions'),
          href: ROUTES.PURCHASE_RECEIPTS,
          icon: HiCollection,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
        },
        {
          name: t('purchases.invoicesMenu', 'Factures fournisseurs'),
          href: ROUTES.PURCHASE_INVOICES,
          icon: HiCurrencyDollar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE'],
        },
        {
          name: t('purchases.paymentsMenu', 'Paiements fournisseurs'),
          href: ROUTES.PURCHASE_PAYMENTS,
          icon: HiCurrencyDollar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE'],
        },
        {
          name: t('purchases.tejMenu', 'Export TEJ (RAS)'),
          href: ROUTES.PURCHASE_TEJ_EXPORT,
          icon: HiDocumentText,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE'],
        },
        {
          name: t('purchases.regularizationMenu', 'Etat fournisseur'),
          href: ROUTES.PURCHASE_PAYMENTS_REGULARIZATION,
          icon: HiCurrencyDollar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE'],
        },
        {
          name: t('purchases.returnsMenu', 'Retours fournisseur'),
          href: ROUTES.PURCHASE_RETURNS,
          icon: HiReply,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
        },
      ],
    },
    {
      section: 'Caisse (POS)',
      items: [
        {
          name: 'Point de Vente',
          href: '/caisse',
          icon: HiCurrencyDollar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_CASHIER', 'ROLE_CAISSE_ADMIN'],
        },
        {
          name: 'Rapports X/Z',
          href: '/caisse/reports',
          icon: HiChartBar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_CASHIER', 'ROLE_CAISSE_ADMIN'],
        },
        {
          name: 'Points de caisse',
          href: '/caisse/points',
          icon: HiOfficeBuilding,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_CAISSE_ADMIN'],
        },
        {
          name: 'Historique sessions',
          href: '/caisse/sessions',
          icon: HiClipboardList,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_CAISSE_ADMIN'],
        },
        {
          name: 'Historique tickets',
          href: '/caisse/tickets',
          icon: HiDocumentText,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_CASHIER', 'ROLE_CAISSE_ADMIN'],
        },
      ],
    },
    {
      section: t('nav.sections.analytics'),
      items: [
        {
          name: t('nav.reports'),
          href: ROUTES.REPORTS,
          icon: HiChartBar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_COMPTABLE'],
        },
        {
          name: t('nav.analytics'),
          href: ROUTES.ANALYTICS,
          icon: HiTrendingUp,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT'],
        },
        {
          name: t('nav.stockAnalytics'),
          href: ROUTES.STOCK_ANALYTICS,
          icon: HiChartBar,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT', 'ROLE_MAGASINIER'],
        },
      ],
    },
    {
      name: t('nav.chatIA'),
      href: ROUTES.CHAT_IA,
      icon: HiChatAlt2,
      roles: ['*'],
    },
    {
      section: t('nav.sections.settings'),
      items: [
        {
          name: t('nav.profile'),
          href: ROUTES.PROFILE,
          icon: HiUser,
          roles: ['*'],
        },
        {
          name: t('nav.settings'),
          href: ROUTES.SETTINGS,
          icon: HiCog,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT'],
        },
        {
          name: 'Caissiers',
          href: ROUTES.SETTINGS_CASHIERS,
          icon: HiUsers,
          roles: ['ROLE_ADMIN', 'ROLE_GERANT'],
        },
      ],
    },
    {
      section: 'Super Admin',
      items: [
        {
          name: 'Utilisateurs',
          href: '/super-admin/users',
          icon: HiUser,
          roles: ['ROLE_SUPER_ADMIN'],
        },
        {
          name: 'Entreprises',
          href: '/super-admin/entreprises',
          icon: HiOfficeBuilding,
          roles: ['ROLE_SUPER_ADMIN'],
        },
        {
          name: 'Rôles',
          href: '/super-admin/roles',
          icon: HiCog,
          roles: ['ROLE_SUPER_ADMIN'],
        },
      ],
    },
  ], [alertsBadge, t])

  // Filtrer la navigation selon les rôles de l'utilisateur
  const navigation = useMemo(() => {
    const userRoles = user?.roles || []
    
    const hasAccess = (requiredRoles) => {
      if (requiredRoles.includes('*')) return true
      return requiredRoles.some(role => userRoles.includes(role))
    }

    return allNavigationItems.filter(item => {
      if (item.section) {
        // C'est une section avec items
        const filteredItems = item.items.filter(subItem => hasAccess(subItem.roles))
        if (filteredItems.length === 0) return false
        return { ...item, items: filteredItems }
      }
      // C'est un item simple
      return hasAccess(item.roles)
    }).map(item => {
      if (item.section) {
        return {
          ...item,
          items: item.items.filter(subItem => hasAccess(subItem.roles)),
        }
      }
      return item
    })
  }, [allNavigationItems, user])

  // Desktop sidebar
  const DesktopSidebar = () => (
    <div
      className={classNames(
        'hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 bg-white border-r border-gray-200 transition-all duration-300',
        {
          'w-64': !isCollapsed,
          'w-20': isCollapsed,
        }
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
        <NavLink
          to={ROUTES.HOME}
          className="flex items-center gap-3 font-semibold text-primary-600"
        >
          <div className="flex-shrink-0 w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold">
            C
          </div>
          {!isCollapsed && <span className="text-xl">{t('common.appName')}</span>}
        </NavLink>
        
        <button
          onClick={onToggle}
          className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <HiMenu className="w-5 h-5" />
          ) : (
            <HiX className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
        {navigation.map((item, index) => {
          // Section avec items
          if (item.section) {
            return (
              <div key={index}>
                {!isCollapsed && (
                  <div className="px-3 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {item.section}
                  </div>
                )}
                {isCollapsed && <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />}
                <div className="space-y-1">
                  {item.items.map(subItem => (
                    <NavLink
                      key={subItem.href}
                      to={subItem.href}
                      className={({ isActive }) =>
                        classNames(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                          {
                            'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400': isActive,
                            'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800': !isActive,
                            'justify-center': isCollapsed,
                          }
                        )
                      }
                      title={isCollapsed ? subItem.name : undefined}
                    >
                      <subItem.icon className="flex-shrink-0 w-5 h-5" aria-hidden="true" />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1">{subItem.name}</span>
                          {subItem.badge && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                              {subItem.badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            )
          }
          
          // Item simple
          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                classNames(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                  {
                    'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400': isActive,
                    'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800': !isActive,
                    'justify-center': isCollapsed,
                  }
                )
              }
              title={isCollapsed ? item.name : undefined}
            >
              <item.icon className="flex-shrink-0 w-5 h-5" aria-hidden="true" />
              {!isCollapsed && (
                <>
                  <span className="flex-1">{item.name}</span>
                  {item.badge && (
                    <span className="inline-flex min-w-[1.5rem] justify-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            © 2024 Carthago ERP
          </div>
        </div>
      )}
    </div>
  )

  // Mobile sidebar
  const MobileSidebar = () => (
    <>
      {/* Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onMobileMenuClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={classNames(
          'lg:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out',
          {
            'translate-x-0': isMobileMenuOpen,
            '-translate-x-full': !isMobileMenuOpen,
          }
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <NavLink
            to={ROUTES.HOME}
            className="flex items-center gap-3 font-semibold text-primary-600"
            onClick={onMobileMenuClose}
          >
            <div className="flex-shrink-0 w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold">
              C
            </div>
            <span className="text-xl">{t('common.appName')}</span>
          </NavLink>
          
          <button
            onClick={onMobileMenuClose}
            className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Close menu"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
          {navigation.map((item, index) => {
            // Section avec items
            if (item.section) {
              return (
                <div key={index}>
                  <div className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {item.section}
                  </div>
                  <div className="space-y-1">
                    {item.items.map(subItem => (
                      <NavLink
                        key={subItem.href}
                        to={subItem.href}
                        onClick={onMobileMenuClose}
                        className={({ isActive }) =>
                          classNames(
                            'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                            {
                              'bg-primary-50 text-primary-600': isActive,
                              'text-gray-700 hover:bg-gray-100 hover:text-gray-900': !isActive,
                            }
                          )
                        }
                      >
                        <subItem.icon className="flex-shrink-0 w-5 h-5" aria-hidden="true" />
                        <span className="flex-1">{subItem.name}</span>
                        {subItem.badge && (
                          <span className="inline-flex min-w-[1.5rem] justify-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                            {subItem.badge}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )
            }
            
            // Item simple
            return (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onMobileMenuClose}
                className={({ isActive }) =>
                  classNames(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    {
                      'bg-primary-50 text-primary-600': isActive,
                      'text-gray-700 hover:bg-gray-100 hover:text-gray-900': !isActive,
                    }
                  )
                }
              >
                <item.icon className="flex-shrink-0 w-5 h-5" aria-hidden="true" />
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="inline-flex min-w-[1.5rem] justify-center rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            © 2024 Carthago ERP
          </div>
        </div>
      </div>
    </>
  )

  return (
    <>
      <DesktopSidebar />
      <MobileSidebar />
    </>
  )
}

export default Sidebar


